import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import {
  ChatEventChatCreatedContentDto,
  ChatEventChatDeletedContentDto,
  ChatEventChatEditedContentDto,
  ChatEventChatMemberAddedContentDto,
  ChatEventChatMemberRemovedContentDto,
  ChatEventChatSettingsChangedContentDto,
  ChatEventDirectChatCreatedContentDto,
  ChatEventDto,
  ChatEventFileGroupReceivedContentDto,
  ChatEventFileReceivedContentDto,
  ChatEventLastMessageChangedContentDto,
  ChatEventLastReadMessageChangedContentDto,
  ChatEventReactionsChangedContentDto,
  ChatEventTextDeletedContentDto,
  ChatEventTextReceivedContentDto,
  ChatEventTextUpdatedContentDto,
  ChatEventTypeEnum,
  ChatEventUpdateMessengerBadgeContentDto,
  ChatEventUpdatePublicChatBadgeContentDto,
  ChatEventUserEditedContentDto,
  ChatEventUserLeftChatContentDto,
  ChatEventUserOfflineContentDto,
  ChatEventUserOnlineContentDto,
  ChatTypeEnum,
  LastChatMessageResponseDto,
} from '@src/api-messenger';
import { logger } from '@src/utils';
import { AuthUserService } from '@src/app/modules/auth';

import { MessageModel } from '../message';
import { mapMessageFileToMessage, mapMessageGroupFilesToMessage, mapMessageTextToMessage } from '../utils';
import { ConnectionState, UpdateUserModel } from '../types';

import { StoreService } from './store.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private readonly store = inject(StoreService);
  private readonly api = inject(ApiService);
  private readonly authUserService = inject(AuthUserService);

  readonly authUser$ = this.authUserService.authUser$;

  publicChatUnreadMessageCount$ = new BehaviorSubject<number>(0);
  unreadChatsCount$ = new BehaviorSubject<number>(0);

  state = signal<ConnectionState>(ConnectionState.Disconnected);

  private connection?: HubConnection;

  get connectionId() {
    return this.connection?.connectionId;
  }

  get authUserId() {
    return this.authUser$?.value?.id;
  }

  async connect(messengerUrl: string, access_token: string, authUserId: string) {
    if (this.connection) {
      return;
    }

    const url = messengerUrl + '/api/chat-hub';
    this.connection = new HubConnectionBuilder()
      .withUrl(url, { accessTokenFactory: () => access_token })
      .withServerTimeout(60000)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Trace)
      .build();

    logger('WebSocketService new connection');

    this.connection?.on('ReceiveEvent', (receiveEvent: ChatEventDto) => {
      logger('ReceiveEvent', receiveEvent);

      // ===================== Сообщения =====================
      if (receiveEvent.eventType === ChatEventTypeEnum.TextReceived) {
        const content: ChatEventTextReceivedContentDto = receiveEvent.textReceivedContent;
        const newMessage: MessageModel = mapMessageTextToMessage(content, receiveEvent.createdAt);
        this.store.setChatMessagesState(content.chatId, [newMessage]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.FileReceived) {
        const content: ChatEventFileReceivedContentDto = receiveEvent.textReceivedContent;
        const newMessage: MessageModel = mapMessageFileToMessage(content, receiveEvent.createdAt);
        this.store.setChatMessagesState(content.chatId, [newMessage]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.FileGroupReceived) {
        const content: ChatEventFileGroupReceivedContentDto = receiveEvent.textReceivedContent;
        const newMessage: MessageModel = mapMessageGroupFilesToMessage(content, receiveEvent.createdAt);
        this.store.setChatMessagesState(content.chatId, [newMessage]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.TextUpdated) {
        const content: ChatEventTextUpdatedContentDto = receiveEvent.textReceivedContent;
        const updatedMessage = this.store.getChatMessage(content.chatId, content.messageId);
        updatedMessage.content.text = content.text;
        this.store.setChatMessagesState(content.chatId, [updatedMessage]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.TextDeleted) {
        const content: ChatEventTextDeletedContentDto = receiveEvent.textReceivedContent;
        this.store.deleteChatMessage(content.chatId, content.messageId);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.LastMessageChanged) {
        const content: ChatEventLastMessageChangedContentDto = receiveEvent.textReceivedContent;
        const lastChatMessage: LastChatMessageResponseDto = {
          contentType: content.contentType,
          messageId: content.messageId,
          timestamp: content.timestamp,
          createdAt: content.createdAt,
          messageText: content.messageText,
          senderUserId: content.senderUserId,
          senderUserName: content.senderUserName,
        };

        this.store.updateChatsState([
          { chatId: content.chatId, unreadMessageCount: content.unreadMessageCount, lastChatMessage },
        ]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.LastReadMessageChanged) {
        const content: ChatEventLastReadMessageChangedContentDto = receiveEvent.textReceivedContent;
        this.store.updateChatsState([{ chatId: content.chatId, lastReadMessageId: content.messageId }]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.MessengerBadgeUpdated) {
        const content: ChatEventUpdateMessengerBadgeContentDto = receiveEvent.textReceivedContent;
        this.unreadChatsCount$.next(content.unreadChatCount);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.MessengerPublicChatBadgeUpdated) {
        const content: ChatEventUpdatePublicChatBadgeContentDto = receiveEvent.textReceivedContent;
        this.publicChatUnreadMessageCount$.next(content.unreadPublicChatMessageCount);
      }

      // ===================== Пользователи =====================
      if (receiveEvent.eventType === ChatEventTypeEnum.UserOnline) {
        const content: ChatEventUserOnlineContentDto = receiveEvent.textReceivedContent;
        this.store.setUserState({ userId: content.userId, userName: content.userName, online: true });
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.UserOffline) {
        const content: ChatEventUserOfflineContentDto = receiveEvent.textReceivedContent;
        this.store.setUserState({ userId: content.userId, userName: content.userName, online: false });
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.UserEdited) {
        const content: ChatEventUserEditedContentDto = receiveEvent.textReceivedContent;
        const { userId, userName, isAvatarExists, chatId } = content;
        const user: UpdateUserModel = {
          userId,
          userName,
          isAvatarExists,
          avatarSrc: undefined,
        };

        this.store.updateUserState(user);
        this.store.updateChatMembersState(chatId, [user]);
        this.store.updateUserNameForChatMessagesState(chatId, user);
      }

      // ===================== Чаты =====================
      if (receiveEvent.eventType === ChatEventTypeEnum.ChatMemberAdded) {
        const content: ChatEventChatMemberAddedContentDto = receiveEvent.textReceivedContent;
        this.store.addChatByAttachId(authUserId, { chatId: content.chatId, chatName: content.chatName });
        this.store.setChatMembersState(content.chatId, [
          { userId: content.userId, userName: content.userName, online: false, isAvatarExists: content.isAvatarExists },
        ]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.DirectChatCreated) {
        const content: ChatEventDirectChatCreatedContentDto = receiveEvent.textReceivedContent;
        const chatName =
          this.authUser$.value?.id === content.creatorUserId ? content.anotherUserName : content.creatorUserName;
        const isAvatarExists =
          this.authUser$.value?.id === content.creatorUserId
            ? content.anotherUserAvatarExists
            : content.creatorAvatarExists;

        this.store.addChatByAttachId(authUserId, {
          chatId: content.chatId,
          chatName,
          chatType: ChatTypeEnum.DirectChat,
          isAvatarExists,
        });

        this.store.setChatMessagesState(content.chatId, []);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.DirectChatEdited) {
        const content: ChatEventDirectChatCreatedContentDto = receiveEvent.textReceivedContent;
        const chatName =
          this.authUser$.value?.id === content.creatorUserId ? content.anotherUserName : content.creatorUserName;
        const isAvatarExists =
          this.authUser$.value?.id === content.creatorUserId
            ? content.anotherUserAvatarExists
            : content.creatorAvatarExists;

        this.store.updateChatsState([
          {
            chatId: content.chatId,
            chatName,
            isAvatarExists,
            avatarSrc: undefined,
          },
        ]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.ChatCreated) {
        const content: ChatEventChatCreatedContentDto = receiveEvent.textReceivedContent;
        this.store.addChatByAttachId(authUserId, content);
        this.store.setChatMessagesState(content.chatId, []);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.ChatEdited) {
        const content: ChatEventChatEditedContentDto = receiveEvent.textReceivedContent;
        this.store.updateChatsState([{ ...content, avatarSrc: undefined }]);

        // TODO: перенести в ChatComponent. Скорее всего лучше бэку отправлять сигнал об изменении permissions в чате
        this.api.getChatPermissions(content.chatId).subscribe();
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.ChatDeleted) {
        const content: ChatEventChatDeletedContentDto = receiveEvent.textReceivedContent;
        this.store.deleteChatByAttachId(authUserId, content.chatId);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.ChatMemberRemoved) {
        const content: ChatEventChatMemberRemovedContentDto = receiveEvent.textReceivedContent;
        this.store.deleteChatMember(content.chatId, content.userId);
        if (content.userId === authUserId) {
          this.store.deleteChatByAttachId(authUserId, content.chatId);
        }
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.ChatAdminAdded) {
        const content: ChatEventChatMemberAddedContentDto = receiveEvent.textReceivedContent;
        this.store.updateChatsState([{ chatId: content.chatId, chatName: content.chatName }]);
        this.store.updateChatMembersState(content.chatId, [
          {
            userId: content.userId,
            userName: content.userName,
            isChatAdmin: true,
            isAvatarExists: content.isAvatarExists,
          },
        ]);

        // TODO: перенести в ChatComponent. Скорее всего лучше бэку отправлять сигнал об изменении permissions в чате
        this.api.getChatPermissions(content.chatId).subscribe();
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.ChatAdminRemoved) {
        const content: ChatEventChatMemberRemovedContentDto = receiveEvent.textReceivedContent;
        this.store.updateChatMembersState(content.chatId, [{ userId: content.userId, isChatAdmin: false }]);

        // TODO: перенести в ChatComponent. Скорее всего лучше бэку отправлять сигнал об изменении permissions в чате
        this.api.getChatPermissions(content.chatId).subscribe();
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.ChatSettingsChanged) {
        const content: ChatEventChatSettingsChangedContentDto = receiveEvent.textReceivedContent;
        this.store.updateChatsState([{ chatId: content.chatId, settings: content.settings }]);
      }

      if (receiveEvent.eventType === ChatEventTypeEnum.UserLeftChat) {
        const content: ChatEventUserLeftChatContentDto = receiveEvent.textReceivedContent;
        this.store.deleteChatMember(content.chatId, content.userId);
        if (content.userId === authUserId) {
          this.store.deleteChatByAttachId(authUserId, content.chatId);
        }
      }

      // ===================== Реакции =====================
      if (receiveEvent.eventType === ChatEventTypeEnum.ReactionsChanged) {
        const content: ChatEventReactionsChangedContentDto = receiveEvent.textReceivedContent;
        this.store.setMessageReactionsState(content.chatId, content.messageId, content.emodjies);
      }
    });

    this.connection?.onclose(() => {
      this.state.set(ConnectionState.Disconnected);
      logger('WebSocketService connection onclose');
    });

    try {
      await this.connection?.start();
      logger('WebSocketService start connection');
      this.state.set(ConnectionState.Connected); // HACK: меняем в ручную, так как нет события в connection
    } catch (error) {
      console.error('Error while starting connection: ' + error);
    }
  }

  async closeConnection() {
    this.state.set(ConnectionState.Disconnecting);
    await this.connection?.stop();
    logger('WebSocketService stop connection');
    this.connection = undefined;
  }
}
